import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { MapPin, Phone, MessageCircle, Mail, Clock, Star, BadgeCheck, ArrowLeft, Share2, Store, ChevronRight, Facebook, Instagram, Youtube } from "lucide-react";

function StarRow({ rating, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={n <= Math.round(rating) ? "text-amber-500 fill-amber-500" : "text-slate-300"} style={{ width: size, height: size }}/>
      ))}
    </div>
  );
}

export default function BusinessProfilePage() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/biz/${slug}`).then((r) => setBiz(r.data)).catch(() => setBiz(null)).finally(() => setLoading(false));
  useEffect(() => { load(); api.post(`/biz/${slug}/view`).catch(() => {}); /* eslint-disable-next-line */ }, [slug]);

  const submitReview = async () => {
    if (!reviewForm.name.trim()) { toast.error("नाम दर्ज करें"); return; }
    setSubmitting(true);
    try {
      await api.post(`/biz/${slug}/review`, reviewForm);
      toast.success("धन्यवाद! आपकी review जुड़ गई");
      setReviewForm({ name: "", rating: 5, comment: "" });
      load();
    } catch (e) { toast.error("Review जोड़ने में समस्या"); }
    finally { setSubmitting(false); }
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: biz?.name, text: biz?.tagline || biz?.name, url: shareUrl }); } catch {}
    } else {
      navigator.clipboard.writeText(shareUrl); toast.success("Link copied!");
    }
  };
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(shareUrl)}`;

  // WhatsApp pre-filled greeting message + deep-link
  const waMsg = biz ? `नमस्ते ${biz.name} 🙏%0A%0Aमैंने INDIAN-NEWS-21 पर आपका profile देखा और मुझे आपकी सेवा के बारे में जानकारी चाहिए।%0A%0A(via https://indiannews21.com/biz/${biz.slug})` : "";
  const waNumber = biz?.whatsapp?.replace(/[^0-9]/g, "") || biz?.phone?.replace(/[^0-9]/g, "") || "";
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : null;

  if (loading) return <><PublicHeader /><div className="max-w-4xl mx-auto p-10 text-center text-slate-500">Loading…</div><PublicFooter /></>;
  if (!biz) return <><PublicHeader /><div className="max-w-4xl mx-auto p-10 text-center">
    <Store className="w-14 h-14 text-slate-400 mx-auto mb-3"/>
    <div className="font-devnagri-heading text-2xl font-black text-slate-700">Business नहीं मिला</div>
    <Link to="/directory" className="inline-block mt-4 text-[#DC2626] font-bold hover:underline">← व्यापार निर्देशिका पर वापस जाएँ</Link>
  </div><PublicFooter /></>;

  return (
    <>
      <PublicHeader />
      {/* Cover */}
      <section className="relative bg-gradient-to-br from-slate-900 to-slate-700 overflow-hidden" data-testid="biz-profile">
        {biz.cover_image && <img src={biz.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40"/>}
        <div className="relative max-w-6xl mx-auto px-4 py-10 text-white">
          <Link to="/directory" className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-100 text-xs font-bold mb-3"><ArrowLeft className="w-3 h-3"/>निर्देशिका</Link>
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-2xl bg-white shadow-2xl overflow-hidden shrink-0 ring-4 ring-amber-400/40 flex items-center justify-center">
              {biz.logo ? <img src={biz.logo} alt={biz.name} className="w-full h-full object-cover"/> : <Store className="w-10 h-10 text-slate-400"/>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-300">{biz.category}</div>
                {biz.verified && <span className="inline-flex items-center gap-1 bg-blue-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded uppercase"><BadgeCheck className="w-2.5 h-2.5"/>Verified</span>}
                {biz.listing_plan === "gold" && <span className="bg-gradient-to-r from-amber-500 to-orange-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded uppercase">Gold</span>}
              </div>
              <h1 className="font-devnagri-heading text-3xl md:text-4xl font-black leading-tight" data-testid="biz-name">{biz.name}</h1>
              {biz.tagline && <p className="text-amber-100/90 mt-1">{biz.tagline}</p>}
              <div className="flex items-center gap-3 mt-2 text-sm">
                <StarRow rating={biz.avg_rating}/>
                <span className="text-slate-200">{biz.avg_rating || "नया"} · {biz.review_count} reviews · {biz.views || 0} views</span>
              </div>
            </div>
            <button onClick={share} className="hidden md:inline-flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur px-3 py-2 text-xs font-bold"><Share2 className="w-3.5 h-3.5"/>Share</button>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Action buttons */}
          <div className="grid grid-cols-3 gap-2">
            {biz.phone && <a href={`tel:${biz.phone}`} className="rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-black py-3 flex items-center justify-center gap-2" data-testid="biz-call"><Phone className="w-4 h-4"/>Call</a>}
            {biz.whatsapp && <a href={`https://wa.me/${biz.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-black py-3 flex items-center justify-center gap-2" data-testid="biz-wa"><MessageCircle className="w-4 h-4"/>WhatsApp</a>}
            {biz.google_maps_url && <a href={biz.google_maps_url} target="_blank" rel="noopener noreferrer" className="rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-black py-3 flex items-center justify-center gap-2" data-testid="biz-map"><MapPin className="w-4 h-4"/>Directions</a>}
          </div>
          {/* Big WhatsApp CTA with pre-filled greeting */}
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer" className="block rounded-2xl overflow-hidden bg-gradient-to-r from-emerald-500 via-green-600 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 text-white shadow-xl transition group" data-testid="biz-wa-cta">
              <div className="flex items-center gap-4 p-4">
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center shrink-0 group-hover:scale-110 transition">
                  <MessageCircle className="w-7 h-7"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black text-emerald-100">
                    <BadgeCheck className="w-3 h-3"/>Green Tick Chat · Verified
                  </div>
                  <div className="font-devnagri-heading text-lg font-black leading-tight mt-0.5">अभी WhatsApp पर पूछें</div>
                  <div className="text-[11px] text-emerald-100/90 mt-0.5">आपका message पहले से ready है — बस send करें, तुरंत जवाब मिलेगा</div>
                </div>
                <ChevronRight className="w-6 h-6 shrink-0 group-hover:translate-x-1 transition"/>
              </div>
            </a>
          )}
          {biz.description && (
            <section className="bg-white rounded-xl border-2 border-slate-100 p-5">
              <h2 className="font-devnagri-heading text-xl font-black text-[#0F172A] mb-2">परिचय</h2>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{biz.description}</p>
            </section>
          )}
          {biz.gallery?.length > 0 && (
            <section className="bg-white rounded-xl border-2 border-slate-100 p-5">
              <h2 className="font-devnagri-heading text-xl font-black text-[#0F172A] mb-3">तस्वीरें</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {biz.gallery.map((src, i) => (
                  <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden bg-slate-100 hover:opacity-80 transition">
                    <img src={src} alt="" className="w-full h-full object-cover"/>
                  </a>
                ))}
              </div>
            </section>
          )}
          {/* Reviews */}
          <section className="bg-white rounded-xl border-2 border-slate-100 p-5" data-testid="biz-reviews">
            <h2 className="font-devnagri-heading text-xl font-black text-[#0F172A] mb-3">Reviews ({biz.review_count})</h2>
            {biz.reviews.length === 0 && <div className="text-sm text-slate-500 italic mb-3">कोई review नहीं — पहले बनें!</div>}
            <div className="space-y-3 mb-5">
              {biz.reviews.map((r) => (
                <div key={r.id} className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-[#0F172A]">{r.name}</div>
                    <StarRow rating={r.rating} size={14}/>
                  </div>
                  {r.comment && <div className="text-sm text-slate-700 mt-1">{r.comment}</div>}
                </div>
              ))}
            </div>
            <div className="border-t-2 border-dashed border-slate-200 pt-4 space-y-2">
              <div className="font-bold text-sm">अपनी Review दें</div>
              <input value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })} placeholder="आपका नाम" className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 text-sm" data-testid="review-name"/>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Rating:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })}>
                    <Star className={`w-6 h-6 ${n <= reviewForm.rating ? "text-amber-500 fill-amber-500" : "text-slate-300"}`}/>
                  </button>
                ))}
              </div>
              <textarea value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} placeholder="कैसा अनुभव रहा?" rows={3} className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 text-sm" data-testid="review-comment"/>
              <button onClick={submitReview} disabled={submitting} className="w-full py-2.5 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white font-black text-sm" data-testid="review-submit">
                {submitting ? "भेजा जा रहा है…" : "Review जमा करें"}
              </button>
            </div>
          </section>
        </div>
        <aside className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-100 p-5">
            <h3 className="font-devnagri-heading text-lg font-black text-[#0F172A] mb-3">संपर्क</h3>
            <div className="space-y-2 text-sm">
              {biz.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#DC2626] shrink-0"/><span>{biz.address}, {biz.city}</span></div>}
              {biz.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-emerald-600"/><a href={`tel:${biz.phone}`} className="hover:text-[#DC2626]">{biz.phone}</a></div>}
              {biz.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-sky-600"/><a href={`mailto:${biz.email}`} className="hover:text-[#DC2626] break-all">{biz.email}</a></div>}
              {biz.hours && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600"/><span>{biz.hours}</span></div>}
            </div>
            {(biz.facebook || biz.instagram || biz.youtube) && (
              <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-100">
                {biz.facebook && <a href={biz.facebook} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-blue-600"><Facebook className="w-4 h-4"/></a>}
                {biz.instagram && <a href={biz.instagram} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-pink-600"><Instagram className="w-4 h-4"/></a>}
                {biz.youtube && <a href={biz.youtube} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-red-600"><Youtube className="w-4 h-4"/></a>}
              </div>
            )}
          </div>
          <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border-2 border-amber-200 rounded-xl p-5 text-center">
            <div className="text-[10px] uppercase tracking-widest font-black text-amber-700 mb-2">Share via QR</div>
            <img src={qrUrl} alt="QR code" className="w-40 h-40 mx-auto rounded-lg bg-white p-2 shadow-md"/>
            <div className="text-[11px] text-slate-600 mt-2">Scan करके यह business card share करें</div>
          </div>
          {biz.amenities?.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-slate-100 p-5">
              <h3 className="font-devnagri-heading text-lg font-black text-[#0F172A] mb-2">सुविधाएँ</h3>
              <div className="flex flex-wrap gap-1.5">
                {biz.amenities.map((a) => <span key={a} className="text-[11px] font-bold bg-emerald-100 text-emerald-800 px-2 py-1 rounded">{a}</span>)}
              </div>
            </div>
          )}
        </aside>
      </main>
      {/* Floating WhatsApp FAB (mobile-primary CTA) */}
      {waLink && (
        <a href={waLink} target="_blank" rel="noopener noreferrer"
           className="fixed right-4 bottom-24 lg:bottom-8 z-40 flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xl ring-4 ring-emerald-100 pl-3 pr-4 py-3 font-black text-sm transition hover:scale-105"
           data-testid="biz-wa-fab">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"/>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"/>
          </span>
          <MessageCircle className="w-5 h-5"/>Chat
        </a>
      )}
      <PublicFooter />
    </>
  );
}
